import { ReactNode, useEffect, useState } from "react";
import { Button, Card, Space, Typography, Upload } from "antd";
import type { UploadProps } from "antd";
import {
  CheckCircleFilled,
  CloseOutlined,
  CloudUploadOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import {
  ACCEPTED_IMAGE_TYPES,
  validateImageFile,
} from "../../services/workflowUpload";
import { toastError } from "../../utils/toast";

const { Text } = Typography;
const { Dragger } = Upload;

interface WorkflowUploadCardProps {
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  file: File | null;
  uploading: boolean;
  uploaded: boolean;
  onFileSelected: (file: File) => void;
  onRemove: () => void;
  onUpload: () => void;
}

const WorkflowUploadCard = ({
  title,
  subtitle,
  icon,
  file,
  uploading,
  uploaded,
  onFileSelected,
  onRemove,
  onUpload,
}: WorkflowUploadCardProps) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const beforeUpload: UploadProps["beforeUpload"] = (selected) => {
    const error = validateImageFile(selected as File);
    if (error) {
      toastError(error);
      return Upload.LIST_IGNORE;
    }
    onFileSelected(selected as File);
    return false;
  };

  return (
    <Card
      size="small"
      title={
        <Space size={10}>
          {icon && <span style={{ color: "#1677ff" }}>{icon}</span>}
          <span>
            <Text strong style={{ display: "block" }}>
              {title}
            </Text>
            {subtitle && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {subtitle}
              </Text>
            )}
          </span>
        </Space>
      }
      extra={
        uploaded ? <CheckCircleFilled style={{ color: "#52c41a", fontSize: 18 }} /> : null
      }
      style={{ borderRadius: 12 }}
    >
      {uploaded ? (
        <Space
          direction="vertical"
          align="center"
          style={{ width: "100%", padding: "20px 0" }}
        >
          <CheckCircleFilled style={{ color: "#52c41a", fontSize: 40 }} />
          <Text strong>Image uploaded</Text>
          <Button type="link" onClick={onRemove}>
            Clear &amp; upload new
          </Button>
        </Space>
      ) : file && previewUrl ? (
        <Space direction="vertical" style={{ width: "100%" }} size={12}>
          <div
            style={{
              position: "relative",
              borderRadius: 10,
              overflow: "hidden",
              border: "1px solid #e2e8f0",
              maxHeight: 240,
            }}
          >
            <img
              src={previewUrl}
              alt={title}
              style={{
                width: "100%",
                maxHeight: 240,
                objectFit: "contain",
                background: "#f8fafc",
                display: "block",
              }}
            />
            {!uploading && (
              <Button
                shape="circle"
                size="small"
                icon={<CloseOutlined />}
                aria-label="Remove image"
                onClick={onRemove}
                style={{ position: "absolute", top: 8, right: 8 }}
              />
            )}
          </div>
          <Button
            type="primary"
            block
            icon={<CloudUploadOutlined />}
            loading={uploading}
            disabled={uploading}
            onClick={onUpload}
          >
            {uploading ? "Uploading…" : "Confirm & Upload"}
          </Button>
        </Space>
      ) : (
        <Dragger
          accept={ACCEPTED_IMAGE_TYPES.join(",")}
          multiple={false}
          showUploadList={false}
          beforeUpload={beforeUpload}
          style={{ borderRadius: 10 }}
        >
          <p className="ant-upload-drag-icon" style={{ marginBottom: 4 }}>
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Click or drag an image to upload</p>
          <p className="ant-upload-hint" style={{ fontSize: 12 }}>
            JPG, PNG, or WEBP · Max 10 MB
          </p>
        </Dragger>
      )}
    </Card>
  );
};

export default WorkflowUploadCard;
